import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import Constants from 'expo-constants';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import { useAuth } from './AuthContext';
import {
  registerPushTokenUseCase,
  removePushTokenUseCase,
  getUnreadNotificationCountUseCase,
} from '../core/dependencies';
import { logger } from '@/src/core/utils';
import { NOTIFICATIONS_PERMISSION_KEY } from '@/src/core/utils/storageKeys';

if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: true,
    }),
  });
}

interface NotificationContextType {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
  expoPushToken: string | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

async function registerForPushNotificationsAsync(): Promise<string | null> {
  if (Platform.OS === 'web') return null;

  if (!Device.isDevice) {
    logger.info('Notifications', 'Must use physical device for push notifications');
    return null;
  }

  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  if (existingStatus !== 'granted') {
    // Only show the native permission dialog if the user has already seen the
    // in-app primer (onboarding step). New users go through onboarding first.
    const hasSeenPrimer = await AsyncStorage.getItem(NOTIFICATIONS_PERMISSION_KEY).catch(() => null);
    if (!hasSeenPrimer) {
      logger.info('Notifications', 'Skipping permission request — user has not seen primer yet');
      return null;
    }
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== 'granted') {
    logger.info('Notifications', 'Push notification permission not granted');
    return null;
  }

  const projectId = Constants.expoConfig?.extra?.eas?.projectId;
  if (!projectId) {
    logger.warn('Notifications', 'No EAS project ID configured');
    return null;
  }

  try {
    const tokenData = await Notifications.getExpoPushTokenAsync({ projectId });
    return tokenData.data;
  } catch (error) {
    // FirebaseApp not initialized — google-services.json missing in this build
    logger.warn('Notifications', 'Failed to get push token', { error });
    return null;
  }
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user, registerBeforeSignOut } = useAuth();
  const router = useRouter();
  const [unreadCount, setUnreadCount] = useState(0);
  const [expoPushToken, setExpoPushToken] = useState<string | null>(null);
  const notificationListener = useRef<Notifications.EventSubscription>();
  const responseListener = useRef<Notifications.EventSubscription>();

  const refreshUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    try {
      const count = await getUnreadNotificationCountUseCase.execute(user.id);
      setUnreadCount(count);
    } catch (error) {
      // Non-critical background fetch; transient network failures are expected
      logger.warn('Notifications', 'Failed to fetch unread count', { error });
    }
  }, [user]);

  // Register push token and set up listeners when user logs in
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setExpoPushToken(null);
      return;
    }

    let isMounted = true;

    const setup = async () => {
      // Register for push notifications
      const token = await registerForPushNotificationsAsync();
      if (token && isMounted) {
        setExpoPushToken(token);
        try {
          await registerPushTokenUseCase.execute(user.id, token, Device.modelName || undefined);
        } catch (error) {
          logger.error('Notifications', 'Failed to register push token', { error });
        }
      }

      // Fetch initial unread count
      if (isMounted) {
        await refreshUnreadCount();
      }
    };

    setup();

    // Push notification listeners are only available on native platforms
    if (Platform.OS !== 'web') {
      // Listen for incoming notifications (foreground)
      notificationListener.current = Notifications.addNotificationReceivedListener(() => {
        if (isMounted) {
          refreshUnreadCount();
        }
      });

      // Listen for notification taps (navigation)
      responseListener.current = Notifications.addNotificationResponseReceivedListener((response) => {
        const data = response.notification.request.content.data;
        if (!data) return;

        const { type, tournamentId, leagueId, userId } = data as Record<string, string>;

        switch (type) {
          case 'tournament_started':
          case 'tournament_completed':
          case 'match_result':
            if (tournamentId) router.push(`/bracket/${tournamentId}` as any);
            break;
          case 'tournament_invitation':
            if (tournamentId) router.push(`/tournament/${tournamentId}` as any);
            break;
          case 'friend_request':
            router.push('/friend-requests' as any);
            break;
          case 'challenge_received':
            router.push('/challenges' as any);
            break;
          case 'league_invitation':
            if (leagueId) router.push(`/league/${leagueId}` as any);
            break;
          case 'team_invitation':
            router.push('/invitations' as any);
            break;
          default:
            router.push('/notifications' as any);
        }
      });
    }

    return () => {
      isMounted = false;
      if (Platform.OS !== 'web') {
        notificationListener.current?.remove();
        responseListener.current?.remove();
      }
    };
  }, [user]);

  // Unlink this device's push token before signing out (RLS needs the session),
  // so the next account on the device doesn't receive the previous user's pushes.
  useEffect(() => {
    if (!user || !expoPushToken) return;

    return registerBeforeSignOut(async () => {
      try {
        await removePushTokenUseCase.execute(user.id, expoPushToken);
      } catch (error) {
        logger.warn('Notifications', 'Failed to remove push token on sign out', { error });
      }
    });
  }, [user, expoPushToken, registerBeforeSignOut]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refreshUnreadCount, expoPushToken }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}
